import { queryOptions } from '@tanstack/react-query';
import api from '../services/api';

/**
 * Shared query definitions. The first key segment is the API resource it comes from (vendors,
 * vendor-requests, ...), which is also how realtime change events name resources -- so one event invalidates
 * every screen showing that resource. Two screens that show the same data must use the same definition, so
 * they share one cache entry and update together.
 */

export const assistantQueries = {
  // Purely for the widget to decide whether to render itself -- the real gate is server-side on
  // every /v1/assistant/chat call (backend/routes/assistant_routes.py), never trusted from here.
  status: () => queryOptions({
    queryKey: ['assistant', 'status'],
    queryFn: async () => (await api.get('/v1/assistant/status')).data as { enabled: boolean },
    staleTime: 60_000,
  }),
};

export interface AIGuardStatus { configured: boolean; ai_mode: 'byok' | 'platform'; reason: string | null }
export const aiGuardQueries = {
  // Lets a button disable itself up front with an explanation instead of letting the user hit a
  // 412. NOT the security boundary -- backend/middleware/ai_guard.py re-checks independently on
  // every gated action regardless of what this reports (see components/AIGuardModal.tsx for the
  // reactive side, which catches every blocked action including ones that don't check this first).
  status: () => queryOptions({
    queryKey: ['ai-guard', 'status'],
    queryFn: async () => (await api.get('/v1/ai-guard/status')).data as AIGuardStatus,
    staleTime: 30_000,
  }),
};

export interface HelpArticle {
  id: string; title: string; summary: string; what: string; why: string;
  steps: string[]; tips?: string[]; path?: string;
}
export interface HelpSection { id: string; title: string; summary: string; articles: HelpArticle[] }

export const helpQueries = {
  // Only decides whether the sidebar shows Help; GET /v1/help re-checks the plan on the server.
  access: () => queryOptions({
    queryKey: ['help', 'access'],
    queryFn: async () => (await api.get('/v1/help/access')).data as { enabled: boolean },
    staleTime: 60_000,
  }),
  guide: () => queryOptions({
    queryKey: ['help', 'guide'],
    queryFn: async () => (await api.get('/v1/help')).data as { sections: HelpSection[]; article_count: number },
    staleTime: 10 * 60_000,
    retry: false,
  }),
};

export const vendorQueries = {
  list: () => queryOptions({
    queryKey: ['vendors', 'list'],
    queryFn: async () => {
      const { data } = await api.get('/v1/vendors/');
      return (data?.vendors ?? (Array.isArray(data) ? data : [])) as any[];
    },
  }),
  archived: () => queryOptions({
    queryKey: ['vendors', 'archived'],
    queryFn: async () => ((await api.get('/v1/vendors/deleted')).data?.vendors || []) as any[],
  }),
  profile: (vendorId: string) => queryOptions({
    queryKey: ['vendors', 'detail', vendorId, 'profile'],
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/profile`)).data,
  }),
  contacts: (vendorId: string) => queryOptions({
    queryKey: ['vendors', 'detail', vendorId, 'contacts'],
    queryFn: async () => ((await api.get(`/v1/vendors/${vendorId}/contacts`)).data?.contacts || []) as any[],
  }),
  categories: () => queryOptions({
    queryKey: ['vendors', 'categories'],
    queryFn: async () => ((await api.get('/v1/vendors/categories')).data?.categories || []) as string[],
    staleTime: 10 * 60_000,
  }),
  businessUnits: () => queryOptions({
    queryKey: ['vendors', 'business-units'],
    queryFn: async () => ((await api.get('/v1/vendors/business-units')).data?.business_units || []) as string[],
    staleTime: 10 * 60_000,
  }),
  riskCategories: () => queryOptions({
    queryKey: ['vendors', 'risk-categories'],
    queryFn: async () => ((await api.get('/v1/vendors/risk-categories')).data?.categories || []) as string[],
    staleTime: 60 * 60_000,
  }),
  fourthPartiesPortfolio: () => queryOptions({
    queryKey: ['vendors', 'fourth-parties-portfolio'],
    queryFn: async () => (await api.get('/v1/fourth-parties/portfolio')).data,
  }),
  postureSummary: (vendorId: string) => queryOptions({
    queryKey: ['vendors', 'detail', vendorId, 'posture-summary'],
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/posture-summary`)).data,
    enabled: !!vendorId,
  }),
};

export const teamQueries = {
  members: () => queryOptions({
    queryKey: ['users', 'team-members'],
    queryFn: async () => ((await api.get('/v1/vendors/team-members')).data?.members || []) as { id: string; name: string; email: string }[],
    staleTime: 5 * 60_000,
  }),
  // Admin-only on the server; anyone else gets 0 rather than an error.
  count: () => queryOptions({
    queryKey: ['users', 'count'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/v1/users');
        const users = data.users ?? (Array.isArray(data) ? data : []);
        return users.length as number;
      } catch {
        return 0;
      }
    },
  }),
};

export interface CommandCenter {
  generated_at: string;
  company_name: string | null;
  vendor_count: number;
  // Total vendors card: added in 30 days and the split by risk band.
  vendor_summary?: { added_30d: number; risk_levels: { low: number; medium: number; high: number; critical: number; unscored: number } };
  last_scan_at: string | null;
  portfolio: { score: number | null; grade: string | null; change_30d: number | null; scanned: number; monthly: (number | null)[] };
  organization: { id: string; name: string; domain: string; score: number | null; grade: string | null; vs_portfolio: number | null } | null;
  kpis: {
    high_critical_vendors: number; open_alerts: number; critical_alerts: number; remediation_open: number;
    remediation_overdue: number; renewals_90d: number; next_renewal: { name: string; date: string } | null;
  };
  attention: { severity: 'critical' | 'high' | 'medium' | 'ai'; icon: string; title: string; detail: string; cta: string; link: string; state?: Record<string, unknown> }[];
  attention_total: number;
  briefing: { points: string[]; ai: boolean; period_label: string | null };
  trend: { months: string[]; portfolio: (number | null)[]; organization: (number | null)[]; portfolio_counts?: number[] };
  grades: { A: number; B: number; C: number; D: number; F: number; unscored: number };
  heat_map: {
    likelihood: { band: number; label: string }[];
    impact: { band: number; label: string }[];
    levels: Record<string, Record<string, 'Low' | 'Medium' | 'High' | 'Critical'>>;
    vendors: {
      id: string; name: string; domain: string; score: number | null; critical: number; high: number;
      impact: number; impact_why: string; likelihood: number; likelihood_why: string; level: string;
      residual_likelihood: number; residual_why: string; residual_level: string; in_remediation: boolean;
    }[];
    not_classified: { id: string; name: string; domain: string; score: number | null; likelihood: number }[];
    not_scanned: { id: string; name: string; domain: string; score: number | null }[];
  };
  categories: { name: string; avg: number; vendors: number }[];
  movers: { declines: Mover[]; improvements: Mover[] };
  pipeline: { requested: number; screened: number; approved: number; onboarded: number; in_review: number; awaiting_onboarding: number; rejected: number };
  upcoming: { date: string; kind: 'scan' | 'renewal' | 'remediation'; title: string; link: string }[];
  fourth_parties: { company: string; domain: string; vendors: number; vendor_names: string[] }[];
  activity: { kind: 'scan' | 'alert' | 'request' | 'remediation'; title: string; at: string | null; link: string | null; severity?: string }[];
  findings: { critical: number; high: number; medium: number; low: number; vendors_with_critical: number };
  lowest: { id: string; name: string; domain: string; score: number; grade: string; critical: number; high: number; last_scan: string | null }[];
  coverage: { total: number; scanned_7d: number; scanned_30d: number; never: number; scheduled: number };
  // See backend/services/financial_exposure.py -- a published-benchmark estimate, not a valuation.
  // vendor_count is only vendors WITH both a completed scan and a submitted questionnaire response
  // (what the totals/average below are built from); total_vendor_count is the whole portfolio, and
  // incomplete_vendor_count is how many were left out for missing one or the other.
  financial_exposure: {
    vendor_count: number;
    total_vendor_count: number;
    incomplete_vendor_count: number;
    total_estimated_exposure_usd_millions: number;
    total_exposure_range: { minimum: number; likely: number; maximum: number };
    average_exposure_range: { minimum: number; likely: number; maximum: number };
    top_contributors: {
      vendor_id: string; vendor_name: string; estimated_exposure_usd_millions: number;
      exposure_range: { minimum: number; likely: number; maximum: number };
      industry_label: string; risk_level: string | null;
    }[];
    sources: { name: string; publisher: string; year: number; note: string }[];
    disclaimer: string;
  };
}
interface Mover { id: string; name: string; domain: string; from: number; to: number; change: number }

export const dashboardQueries = {
  overview: () => queryOptions({
    queryKey: ['dashboard', 'overview'],
    queryFn: async () => (await api.get('/v1/dashboard/overview')).data,
  }),
  // The redesigned Dashboard: one tenant-scoped read (backend services/dashboard_command_center.py).
  commandCenter: () => queryOptions({
    queryKey: ['dashboard', 'command-center'],
    queryFn: async () => (await api.get('/v1/dashboard/command-center')).data as CommandCenter,
    staleTime: 30_000,
  }),
};

export interface SettingsOverview {
  company_name: string;
  created_at: string | null;
  // 'byok' (default) or 'platform' -- see backend Phase 54. Read-only: only the admin app can change it.
  ai_mode: 'byok' | 'platform';
  plan: {
    plan: string; plan_status: string; max_vendors: number; max_users: number; max_indicators: number;
    plan_name?: string; extra_vendors?: number; extra_users?: number;
    trial_end_date: string | null; trial_days_remaining: number;
    indicators_included?: number; indicators_total?: number; all_indicators?: boolean;
  };
  vendor_count: number;
  team: {
    total: number; active: number; pending: number; deactivated: number; admins: number;
    admin_members: { id: string; name: string | null; email: string | null }[];
  };
}

export interface RiskTieringPolicy {
  sensitivity_tiers: Record<string, string>;
  review_cycle_months: Record<string, number>;
  is_customized: boolean;
  default_sensitivity_tiers: Record<string, string>;
  default_review_cycle_months: Record<string, number>;
  sensitivity_options: string[];
  tier_options: string[];
}

// The signed-in user and their tenant's plan (profile drawer, header trial badge). Refetched now and then so a plan or
// trial changed by the platform team shows without signing out.
export const authQueries = {
  me: () => queryOptions({
    queryKey: ['auth', 'me'],
    queryFn: async () => (await api.get('/v1/auth/me')).data as {
      tenant?: { plan?: { plan: string; plan_status: string; trial_end_date: string | null } | null } | null;
      [key: string]: any;
    },
    refetchInterval: 15 * 60_000,
  }),
};

// The main-sidebar Settings page.
export const settingsQueries = {
  // General section -- one read for company profile, plan/usage and team counts.
  overview: () => queryOptions({
    queryKey: ['settings', 'overview'],
    queryFn: async () => (await api.get('/v1/settings')).data as SettingsOverview,
  }),
  // Vendor Risk Tiering section -- how this tenant classifies a vendor's inherent risk (Pre Onboarding,
  // vendor Profile). Never touches the numeric scan score.
  riskTiering: () => queryOptions({
    queryKey: ['settings', 'risk-tiering'],
    queryFn: async () => (await api.get('/v1/settings/risk-tiering')).data as RiskTieringPolicy,
  }),
  // Workflow defaults -- starting values for Add Vendor, Pre Onboarding, Request remediation and Vendor Access.
  defaults: () => queryOptions({
    queryKey: ['settings', 'defaults'],
    queryFn: async () => (await api.get('/v1/settings/defaults')).data as WorkflowDefaults,
    staleTime: 5 * 60_000,
  }),
  // Weekly portfolio briefing agent (see backend services/ai/portfolio_briefing.py) -- on by default,
  // sent through the same channels as Alert delivery.
  portfolioBriefing: () => queryOptions({
    queryKey: ['settings', 'portfolio-briefing'],
    queryFn: async () => (await api.get('/v1/settings/portfolio-briefing')).data as { enabled: boolean },
  }),
  // Configured BYOK AI provider keys (admin only) -- Settings -> AI providers and the setup checklist.
  aiProviders: () => queryOptions({
    queryKey: ['settings', 'ai-providers', 'list'],
    queryFn: async () => ((await api.get('/v1/settings/ai-providers')).data.providers || []) as any[],
  }),
  // Change history (admin only) -- workspace, team, integration and alerting configuration changes.
  history: (group: string) => queryOptions({
    queryKey: ['settings', 'history', group],
    queryFn: async () => (await api.get('/v1/settings/history', { params: { group } })).data as { entries: SettingsHistoryEntry[]; groups: string[] },
  }),
};

export interface WorkflowDefaultValues {
  renewal_notice_days: number;
  screen_on_request: boolean;
  remediation_due_days: number;
  remediation_reminder_days: number;
  access_review_months: number;
}

export interface WorkflowDefaults {
  values: WorkflowDefaultValues;
  defaults: WorkflowDefaultValues;
  choices: Record<string, number[]>;
  is_customized: boolean;
}

/** Platform defaults, used until the workspace's own values have loaded (and if they can't be read). */
export const PLATFORM_WORKFLOW_DEFAULTS: WorkflowDefaultValues = {
  renewal_notice_days: 30, screen_on_request: true, remediation_due_days: 30, remediation_reminder_days: 3, access_review_months: 12,
};

export interface SettingsHistoryEntry {
  id: string; user_id: string | null; user_name: string | null; user_email: string | null; action: string;
  resource_type: string | null; details: string | null; created_at: string; group: string;
}

const newestFirst = (a: any, b: any) =>
  new Date(b.completed_at || b.created_at).getTime() - new Date(a.completed_at || a.created_at).getTime();

export const scanQueries = {
  byDomain: (domain: string) => queryOptions({
    queryKey: ['scans', 'by-domain', domain],
    queryFn: async () => {
      try {
        const { data } = await api.get(`/v1/scans/by-domain/${domain}`);
        const list: any[] = data.scans && Array.isArray(data.scans) ? data.scans : Array.isArray(data) ? data : [];
        return [...list].sort(newestFirst);
      } catch {
        const all = await api.get('/v1/scans');
        return (all.data.scans || []).filter((scan: any) => scan.domain === domain).sort(newestFirst) as any[];
      }
    },
  }),
};

export const vendorRequestQueries = {
  list: (status?: string) => queryOptions({
    queryKey: ['vendor-requests', 'list', status ?? 'all'],
    queryFn: async () => ((await api.get('/v1/vendor-requests/', { params: status ? { status } : undefined })).data?.requests || []) as any[],
  }),
};

export const remediationQueries = {
  list: (vendorId?: string) => queryOptions({
    queryKey: ['remediation', 'list', vendorId ?? 'all'],
    queryFn: async () => (await api.get('/v1/remediation', { params: vendorId ? { vendor_id: vendorId } : undefined })).data as {
      requests: any[]; counts: Record<string, number>;
    },
  }),
  detail: (requestId: string) => queryOptions({
    queryKey: ['remediation', 'detail', requestId],
    queryFn: async () => (await api.get(`/v1/remediation/${requestId}`)).data,
  }),
  // indicator name -> the open request it is part of (the Issues tab's "In remediation" marker).
  vendorRisks: (vendorId: string) => queryOptions({
    queryKey: ['remediation', 'vendor-risks', vendorId],
    queryFn: async () => ((await api.get('/v1/remediation/vendor-risks', { params: { vendor_id: vendorId } })).data?.risks || {}) as
      Record<string, { request_id: string; title: string; status: string }>,
  }),
};

// The vendor Issues tab and the "Request remediation" dialog read the same issues.
export const vendorIssueQueries = {
  issues: (vendorId: string) => queryOptions({
    queryKey: ['vendors', 'detail', vendorId, 'issues'],
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/issues`)).data,
  }),
};

export interface TriageSuggestion {
  id: string; vendor_id: string; vendor_name?: string; vendor_domain?: string; vendor_score?: number | null;
  scan_id: string | null; status: string; title: string; summary: string | null;
  suggested_due_date: string | null; indicator_names: string[];
  groups: { title: string; indicator_names: string[]; reasoning: string }[];
  finding_count: number; created_at: string | null;
}

// The scan-triage agent's draft suggestions: a banner on the vendor's Issues tab (vendor-scoped) and
// a "Suggested" queue on the Remediation page (tenant-wide). Root 'triage-suggestions' matches the
// realtime resource the background agent publishes under when a fresh draft is ready.
export const triageQueries = {
  list: () => queryOptions({
    queryKey: ['triage-suggestions', 'list'],
    queryFn: async () => ((await api.get('/v1/triage-suggestions')).data?.suggestions || []) as TriageSuggestion[],
  }),
  forVendor: (vendorId: string) => queryOptions({
    queryKey: ['triage-suggestions', 'vendor', vendorId],
    queryFn: async () => (await api.get(`/v1/triage-suggestions/vendor/${vendorId}`)).data?.suggestion as TriageSuggestion | null,
  }),
};

export interface AlertFilters {
  status: string; severity: string; vendorId: string; type: string; q: string; offset: number; limit: number;
}

// The Alerting page. Every key starts with 'monitoring' -- the resource name its API writes broadcast under.
export const monitoringQueries = {
  stats: () => queryOptions({
    queryKey: ['monitoring', 'stats'],
    queryFn: async () => (await api.get('/v1/monitoring/stats')).data,
  }),
  alerts: (f: AlertFilters) => queryOptions({
    queryKey: ['monitoring', 'alerts', f],
    queryFn: async () => (await api.get('/v1/monitoring/alerts', {
      params: {
        status: f.status, severity: f.severity || undefined, vendor_id: f.vendorId || undefined,
        alert_type: f.type || undefined, q: f.q || undefined, offset: f.offset, limit: f.limit,
      },
    })).data as { alerts: any[]; total: number; counts: Record<string, number> },
  }),
  alert: (id: string) => queryOptions({
    queryKey: ['monitoring', 'alert', id],
    queryFn: async () => (await api.get(`/v1/monitoring/alerts/item/${id}`)).data,
  }),
  rules: () => queryOptions({
    queryKey: ['monitoring', 'rules'],
    queryFn: async () => ((await api.get('/v1/monitoring/alerts/rules')).data?.rules || []) as any[],
  }),
  silences: () => queryOptions({
    queryKey: ['monitoring', 'silences'],
    queryFn: async () => ((await api.get('/v1/monitoring/silences')).data?.silences || []) as any[],
  }),
  channels: () => queryOptions({
    queryKey: ['monitoring', 'channels'],
    queryFn: async () => ((await api.get('/v1/monitoring/channels')).data?.channels || []) as any[],
  }),
  vendorOverview: () => queryOptions({
    queryKey: ['monitoring', 'vendor-overview'],
    queryFn: async () => ((await api.get('/v1/monitoring/vendor-overview')).data?.vendors || []) as any[],
  }),
};

// The tenant's own company (My Risk). The header chip and the My Risk page share this entry.
export interface MyRiskStatus {
  configured: boolean;
  company_name: string | null;
  suggested_domain: string | null;
  scan_in_progress?: boolean;
  profile: {
    vendor_id: string; name: string; domain: string; score: number | null; risk_level: string | null;
    last_scan: string | null; created_at: string | null;
  } | null;
}
export const myRiskQueries = {
  status: () => queryOptions({
    queryKey: ['my-risk'],
    queryFn: async () => (await api.get('/v1/my-risk')).data as MyRiskStatus,
    // Poll only while a scan of your own domain is running, so the page fills in when it finishes.
    refetchInterval: (query) => (query.state.data?.scan_in_progress ? 10_000 : false),
  }),
};

/* Reports: the data behind generated documents, and the team's export history (audit log). */
export const reportQueries = {
  vendor: (vendorId: string) => queryOptions({
    queryKey: ['reports', 'vendor', vendorId],
    queryFn: async () => (await api.get(`/v1/reports/vendor/${vendorId}`)).data,
    staleTime: 30_000,
  }),
  board: () => queryOptions({
    queryKey: ['reports', 'board'],
    queryFn: async () => (await api.get('/v1/reports/board')).data,
    staleTime: 30_000,
  }),
  portfolio: () => queryOptions({
    queryKey: ['reports', 'portfolio'],
    queryFn: async () => (await api.get('/v1/reports/portfolio')).data,
    staleTime: 30_000,
  }),
  exports: () => queryOptions({
    queryKey: ['reports', 'exports'],
    queryFn: async () => ((await api.get('/v1/reports/exports', { params: { limit: 25 } })).data?.exports ?? []) as any[],
  }),
};
